import { useSignal } from "@preact/signals-react";
import { useEffect, useState } from "react";
import { ErrorField } from "../../../../types/ErrorField";
import TextEdit from "../../../molecules/TextEdit";
import SelectTextField from "../../../molecules/SelectTextField";
import { Auth } from 'aws-amplify';

import { toast } from "react-toastify";

import ATable, { ATableHeader } from "../../../organisms/ATable";
import { FaStar } from "react-icons/fa";
import NewTooltip from "../../../atoms/NewTooltip";
import { HiOutlineTrash } from "react-icons/hi2";

import PresentationModal from "../../../organisms/PresentationModal";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import useCities from "../../../../hooks/useCities";
import MiniLoading from "../../../ions/MiniLoading";
import { useNavigate } from "react-router-dom";
import appConfig from "../../../../config/app-config";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

interface BankAccount {
    id?: string;
    bankAccountId?: string;
    bank: string;
    cityId: number;
    city?: string;
    agency: string;
    account: string;
    active: boolean;
    canRemove: boolean;
    canSetActive: boolean;
    default: boolean;
    isBranch?: boolean;
    branchGovTaxId?: string;
    branchLegalName?: string;
}

interface Bank {
    id: number;
    name: string;
    febraban_id: string;
}

interface Props {
    personId: string;
    personType: 'individual' | 'legal_person';
    provinces: any[];
    banks: Bank[];
    bankAccounts: BankAccount[];
    onBankAccountChange?: () => void; // ✅ Callback para notificar mudanças
    readOnly?: boolean;
}

type BankAccountForm = {
    bankId: string;
    cityId: number;
    agency: string;
    account: string;
    digit: string;
    active: boolean;
    province: string;
    isBranch: boolean;
    branchGovTaxId: string;
    branchLegalName: string;
};

function PersonFormBankAccount({ personId, personType, provinces, banks, bankAccounts, onBankAccountChange, readOnly = false }: Props): JSX.Element {
    // ✅ NOVO: API endpoint unificado para person
    const BASE_URL = `${PERSON_API_URL}/person`;

    const isModalOpen = useSignal<boolean>(false);
    const { fetchCities } = useCities();
    const navigate = useNavigate();

    const cities = useSignal<any[]>([]);
    const isLoadingForm = useSignal<boolean>(false);
    const [rows, setRows] = useState<BankAccount[]>(bankAccounts);

    // ✅ Atualizar rows quando bankAccounts prop mudar - Exibir TODAS (ativas e inativas)
    useEffect(() => {
        setRows(bankAccounts);
    }, [bankAccounts]);

    const isSendingForm = useSignal<boolean>(false);
    const isRemoveModalOpen = useSignal<boolean>(false);
    const confirmBankAccountNumber = useSignal<string>("");
    const selectedBankAccount = useSignal<BankAccount | null>(null);
    const errorFields = useSignal<ErrorField[]>([]);

    const accountForm = useSignal<BankAccountForm>({
        bankId: "",
        cityId: 0,
        agency: "",
        account: "",
        digit: "",
        active: true,
        province: "",
        isBranch: false,
        branchGovTaxId: "",
        branchLegalName: "",
    });

    const resetForm = () => {
        accountForm.value = {
            bankId: "",
            cityId: 0,
            agency: "",
            account: "",
            digit: "",
            active: true,
            province: "",
            isBranch: false,
            branchGovTaxId: "",
            branchLegalName: "",
        };
        errorFields.value = [];
    };

    const headers: ATableHeader[] = [
        {
            id: 'bank',
            title: 'Banco',
            canSort: true,
            field: 'bank',
            width: '200px',
            cssClass: 'w-[200px]',
        },
        {
            id: 'city',
            title: 'Cidade',
            canSort: true,
            field: 'city',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'agency',
            title: 'Agência',
            canSort: true,
            field: 'agency',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'account',
            title: 'Conta',
            canSort: true,
            field: 'account',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'branch',
            title: 'Filial',
            canSort: false,
            field: 'isBranch',
            width: '200px',
            cssClass: 'w-[200px]',
        },
        {
            id: 'active',
            title: 'Ativa',
            canSort: false,
            field: 'active',
            width: '90px',
            cssClass: 'w-[90px]',
        },
        {
            id: 'action',
            title: '',
            canSort: false,
            width: '50px',
        },
    ];

    useEffect(() => {
        window.addEventListener("BankAccountFormTab:openModal", () => isModalOpen.value = true);

        return () => {
            window.removeEventListener("BankAccountFormTab:openModal", () => isModalOpen.value = true);
        };
    }, []);

    const handleInputChange = (field: keyof BankAccountForm, value: string | boolean) => {
        accountForm.value = {
            ...accountForm.value,
            [field]: value,
        };
    };

    const onlyDigits = (value: string) => value.replace(/\D/g, "");
    const onlyDigitOrX = (value: string) => value.replace(/[^0-9xX]/g, "").toUpperCase();

    const formatCnpj = (value: string) => {
        const digits = value.replace(/\D/g, "");
        return digits
            .replace(/^(\d{2})(\d)/, "$1.$2")
            .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
            .replace(/\.(\d{3})(\d)/, ".$1/$2")
            .replace(/(\d{4})(\d)/, "$1-$2")
            .slice(0, 18);
    };

    const isValidCnpj = (cnpj: string): boolean => {
        const digits = cnpj.replace(/\D/g, "");
        if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) return false;
        const calc = (len: number) => {
            let sum = 0;
            let pos = len - 7;
            for (let i = 0; i < len; i++) {
                sum += parseInt(digits[i]) * pos--;
                if (pos < 2) pos = 9;
            }
            const rest = sum % 11;
            return rest < 2 ? 0 : 11 - rest;
        };
        return calc(12) === parseInt(digits[12]) && calc(13) === parseInt(digits[13]);
    };

    const submitForm = async () => {
        // Validar campos de filial
        if (accountForm.value.isBranch) {
            const errors: ErrorField[] = [];
            const rawCnpj = accountForm.value.branchGovTaxId.replace(/\D/g, "");
            if (!rawCnpj) {
                errors.push({ fieldName: 'branchGovTaxId', message: 'CNPJ da filial é obrigatório' });
            } else if (!isValidCnpj(rawCnpj)) {
                errors.push({ fieldName: 'branchGovTaxId', message: 'CNPJ da filial inválido' });
            }
            if (!accountForm.value.branchLegalName.trim()) {
                errors.push({ fieldName: 'branchLegalName', message: 'Razão social da filial é obrigatória' });
            }
            if (errors.length > 0) {
                errorFields.value = errors;
                return;
            }
        }

        isSendingForm.value = true;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${personId}/bank-accounts`, {
                method: 'POST',
                headers: {
                    'x-api-key': PERSON_API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...accountForm.value,
                    cityId: parseInt(accountForm.value.cityId as unknown as string),
                    branchGovTaxId: accountForm.value.isBranch ? accountForm.value.branchGovTaxId.replace(/\D/g, "") : null,
                    branchLegalName: accountForm.value.isBranch ? accountForm.value.branchLegalName : null,
                }),
            });

            if (response.ok) {
                toast.success('Conta bancária cadastrada com sucesso!');
                isModalOpen.value = false;
                resetForm();

                if (onBankAccountChange) {
                    onBankAccountChange();
                }
            } else if (response.status === 401) {
                toast.error('A sessão expirou. Faça login novamente.');
                navigate('/login');
            } else if (response.status === 409) {
                const { errors, message } = await response.json();
                toast.error(message);
                errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
            } else {
                toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
            }
        } catch (error) {
            toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        }
        isSendingForm.value = false;
    };

    const toggleAccountActivation = async (bankAccountId: string, currentStatus: boolean) => {
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${BASE_URL}/${personId}/bank-account-toggle`, {
                method: 'POST',
                headers: {
                    'x-api-key': PERSON_API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ bankAccountId }),
            });

            if (response.ok) {
                const action = currentStatus ? 'desativada' : 'ativada';
                toast.success(`Conta bancária ${action} com sucesso!`);

                if (onBankAccountChange) {
                    onBankAccountChange();
                }
            } else {
                toast.error("Erro ao alterar status da conta bancária!");
            }
        } catch (error) {
            toast.error("Erro ao alterar status da conta bancária!");
        }
    };

    const removeAccount = async () => {
        if (!selectedBankAccount.value) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${personId}/bank-accounts/${selectedBankAccount.value.bankAccountId}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': PERSON_API_KEY || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                toast.success('Conta bancária removida com sucesso!');
                isRemoveModalOpen.value = false;
                selectedBankAccount.value = null;
                confirmBankAccountNumber.value = "";

                if (onBankAccountChange) {
                    onBankAccountChange();
                }
            } else {
                toast.error("Erro ao remover conta bancária!");
            }
        } catch (error) {
            toast.error("Erro ao remover conta bancária!");
        }
    };

    const openRemoveModal = (bankAccount: BankAccount) => {
        selectedBankAccount.value = bankAccount;
        isRemoveModalOpen.value = true;
    };

    const renderRows = (data: BankAccount[]) => {
        return data.map((row, index) => (
            <tr key={row.bankAccountId || row.id || index} className={`${row.active === false ? 'bg-gray-200 opacity-60' : index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300`}>
                <td className="py-2 px-4 text-center">{row.bank}</td>
                <td className="py-2 px-4 text-center">{row.city}</td>
                <td className="py-2 px-4 text-center">{row.agency}</td>
                <td className="py-2 px-4 text-center">{row.account}</td>
                <td className="py-2 px-4 text-center">
                    {row.isBranch ? (
                        <span className="text-xs text-orange-700 bg-orange-50 px-2 py-0.5 rounded" title={`${row.branchGovTaxId} — ${row.branchLegalName}`}>
                            {row.branchLegalName}
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400">Matriz</span>
                    )}
                </td>
                <td className="py-2 px-4 text-center">
                    {row.active === false && (
                        <span className="text-xs text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30 px-2 py-0.5 rounded mr-1">Inativa</span>
                    )}
                    {row.default && (
                        <span className="text-xs text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/30 px-2 py-0.5 rounded mr-1">Principal</span>
                    )}
                    {!readOnly && row.canSetActive && (
                        <NewTooltip content={row.active ? "Desativar conta" : "Ativar conta"}>
                            <button
                                id={`btn-toggle-bank-${row.bankAccountId}`}
                                onClick={() => toggleAccountActivation(row.bankAccountId!, row.active)}
                                className={row.active ? "text-yellow-500 hover:text-gray-400" : "text-gray-400 hover:text-yellow-500"}
                            >
                                <FaStar />
                            </button>
                        </NewTooltip>
                    )}
                </td>
                <td className="py-2 px-4 text-center">
                    {!readOnly && row.canRemove && (
                        <NewTooltip content="Remover conta">
                            <button
                                id={`btn-remove-bank-${row.bankAccountId}`}
                                onClick={() => openRemoveModal(row)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <HiOutlineTrash />
                            </button>
                        </NewTooltip>
                    )}
                </td>
            </tr>
        ));
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Contas Bancárias</h3>
                {!readOnly &&
                    <button
                        id="btn-add-bank-account"
                        onClick={() => isModalOpen.value = true}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Nova conta bancária
                    </button>
                }
            </div>

            <ATable headers={headers}>
                {rows.length > 0 ? (
                    renderRows(rows)
                ) : (
                    <tr>
                        <td colSpan={headers.length} className="text-center py-4 text-gray-500">
                            Nenhuma conta bancária cadastrada
                        </td>
                    </tr>
                )}
            </ATable>

            {/* Modal de Nova Conta */}
            <PresentationModal
                id="bank-account-modal"
                show={isModalOpen.value}
                onClose={() => {
                    isModalOpen.value = false;
                    resetForm();
                }}
                header="Nova Conta Bancária"
            >
                <div className="p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <SelectTextField
                            id="bank-select"
                            label="Banco"
                            value={accountForm.value.bankId}
                            onChange={(e) => handleInputChange('bankId', e.target.value)}
                            items={(banks || []).map(bank => ({ value: bank.id.toString(), label: bank.name }))}
                            errorMessage={getErrorMessage(errorFields.value, 'bankId')}
                        />

                        <SelectTextField
                            id="province"
                            label="UF"
                            value={accountForm.value.province}
                            onChange={async (e) => {
                                const value = e.target.value;
                                handleInputChange('province', value);
                                isLoadingForm.value = true;
                                const citiesData = await fetchCities(value);
                                cities.value = citiesData;
                                isLoadingForm.value = false;
                            }}
                            items={(provinces || []).map(province => ({ value: province.initials, label: province.initials }))}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="relative">
                            <SelectTextField
                                id="cityId"
                                label="Cidade"
                                value={accountForm.value.cityId.toString()}
                                onChange={(e) => handleInputChange('cityId', e.target.value)}
                                items={cities.value.map(city => ({ value: city.id.toString(), label: city.name }))}
                                disabled={isLoadingForm.value}
                            />
                            {isLoadingForm.value && (
                                <div className="absolute right-2 top-8">
                                    <MiniLoading />
                                </div>
                            )}
                        </div>

                        <TextEdit
                            id="agency"
                            label="Agência"
                            value={accountForm.value.agency}
                            onChange={(e) => handleInputChange('agency', onlyDigits(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, 'agency')}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <TextEdit
                            id="account"
                            label="Conta Corrente"
                            value={accountForm.value.account}
                            onChange={(e) => handleInputChange('account', onlyDigits(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, 'account')}
                        />

                        <TextEdit
                            id="account-digit"
                            label="Dígito"
                            value={accountForm.value.digit}
                            onChange={(e) => handleInputChange('digit', onlyDigitOrX(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, 'digit')}
                        />
                    </div>

                    {/* Checkbox Filial */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                id="is-branch"
                                type="checkbox"
                                checked={accountForm.value.isBranch}
                                onChange={(e) => {
                                    handleInputChange('isBranch', e.target.checked);
                                    if (!e.target.checked) {
                                        handleInputChange('branchGovTaxId', '');
                                        handleInputChange('branchLegalName', '');
                                    }
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Esta conta pertence a uma filial</span>
                        </label>
                    </div>

                    {accountForm.value.isBranch && (
                        <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
                            <TextEdit
                                id="branch-gov-tax-id"
                                label="CNPJ da Filial"
                                value={formatCnpj(accountForm.value.branchGovTaxId)}
                                onChange={(e) => handleInputChange('branchGovTaxId', onlyDigits(e.target.value).slice(0, 14))}
                                errorMessage={getErrorMessage(errorFields.value, 'branchGovTaxId')}
                                maxLength={18}
                            />
                            <TextEdit
                                id="branch-legal-name"
                                label="Razão Social da Filial"
                                value={accountForm.value.branchLegalName}
                                onChange={(e) => handleInputChange('branchLegalName', e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, 'branchLegalName')}
                            />
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <button
                            id="btn-cancel-bank-account"
                            onClick={() => {
                                isModalOpen.value = false;
                                resetForm();
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Cancelar
                        </button>
                        <button
                            id="btn-save-bank-account"
                            onClick={submitForm}
                            disabled={isSendingForm.value}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {isSendingForm.value ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </PresentationModal>

            {/* Modal de Confirmação de Remoção */}
            <PresentationModal
                id="remove-bank-account-modal"
                show={isRemoveModalOpen.value}
                onClose={() => {
                    isRemoveModalOpen.value = false;
                    selectedBankAccount.value = null;
                    confirmBankAccountNumber.value = "";
                }}
                header="Confirmar Remoção"
            >
                <div className="p-4">
                    <p className="mb-4">
                        Para confirmar a remoção da conta bancária, digite o número da conta:
                    </p>
                    <p className="font-semibold mb-4">
                        {selectedBankAccount.value?.account}
                    </p>

                    <TextEdit
                        id="confirm-account-number"
                        label="Confirme o número da conta"
                        value={confirmBankAccountNumber.value}
                        onChange={(e) => confirmBankAccountNumber.value = e.target.value}
                    />

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            id="btn-cancel-remove-bank"
                            onClick={() => {
                                isRemoveModalOpen.value = false;
                                selectedBankAccount.value = null;
                                confirmBankAccountNumber.value = "";
                            }}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Cancelar
                        </button>
                        <button
                            id="btn-confirm-remove-bank"
                            onClick={removeAccount}
                            disabled={confirmBankAccountNumber.value !== selectedBankAccount.value?.account}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                        >
                            Confirmar Remoção
                        </button>
                    </div>
                </div>
            </PresentationModal>
        </div>
    );
}

export default PersonFormBankAccount;
